import React, { useRef } from "react";
import { ChevronRight, ChevronDown, GitBranch, HelpCircle, Hourglass } from "lucide-react";
import { ACTION_CATEGORIES, QUICK_ACTIONS } from "./catalog";
import { ActionDef } from "./types";
import { cn } from "../../../../utils/function";

const QUICK_ICONS: Record<string, React.ComponentType<any>> = {
  branch: GitBranch,
  condition: HelpCircle,
  wait: Hourglass,
};

// How far the pointer has to move (px) before a press-and-hold turns into a
// drag rather than a click. Below this it's treated as a plain click.
const DRAG_THRESHOLD = 4;

// Persistent left palette — always the same content regardless of what's
// selected on canvas (matches the reference design). Every item supports two
// ways in: click it after picking a "+" slot on the canvas (existing flow —
// `disabled` only dims/blocks the click path when no slot is active), or
// press-drag it straight onto a "+" slot, which works regardless of whether a
// slot was pre-selected.
//
// This used the native HTML5 Drag and Drop API originally, but that turned
// out not to reliably fire a dragstart at all for some pointing devices
// (notably trackpads) — the press-drag gesture just fell through to a plain
// click. Pointer Events (down/move/up, backed by setPointerCapture) don't
// have that failure mode and unify mouse/trackpad/touch, so the drag
// gesture is hand-rolled here instead of relying on the browser's native DnD.
const AddPanel: React.FC<{
  onPick: (action: ActionDef) => void;
  disabled: boolean;
  onDragStart: (action: ActionDef, x: number, y: number) => void;
  onDragMove: (x: number, y: number) => void;
  onDragEnd: (x: number, y: number) => void;
}> = ({ onPick, disabled, onDragStart, onDragMove, onDragEnd }) => {
  const [open, setOpen] = React.useState(true);

  // One shared ref (not per-item state) since only one item can ever be
  // pressed at a time, and it must survive re-renders triggered mid-drag by
  // onDragMove — a ref does, a plain closure variable captured at render
  // time wouldn't.
  const pressRef = useRef<{ action: ActionDef; startX: number; startY: number; dragging: boolean; pointerId: number } | null>(null);

  const handlePointerDown = (action: ActionDef) => (e: React.PointerEvent<HTMLButtonElement>) => {
    if (action.status === "planned") return; // not backed yet — no click, no drag
    if (e.pointerType === "mouse" && e.button !== 0) return;
    pressRef.current = { action, startX: e.clientX, startY: e.clientY, dragging: false, pointerId: e.pointerId };
    e.currentTarget.setPointerCapture(e.pointerId);
  };

  const handlePointerMove = (e: React.PointerEvent<HTMLButtonElement>) => {
    const press = pressRef.current;
    if (!press || press.pointerId !== e.pointerId) return;
    if (!press.dragging) {
      if (Math.hypot(e.clientX - press.startX, e.clientY - press.startY) < DRAG_THRESHOLD) return;
      press.dragging = true;
      onDragStart(press.action, e.clientX, e.clientY);
    }
    e.preventDefault();
    onDragMove(e.clientX, e.clientY);
  };

  const handlePointerUp = (e: React.PointerEvent<HTMLButtonElement>) => {
    const press = pressRef.current;
    if (!press || press.pointerId !== e.pointerId) return;
    pressRef.current = null;
    if (press.dragging) {
      onDragEnd(e.clientX, e.clientY);
    } else {
      onPick(press.action);
    }
  };

  const handlePointerCancel = (e: React.PointerEvent<HTMLButtonElement>) => {
    const press = pressRef.current;
    if (!press || press.pointerId !== e.pointerId) return;
    pressRef.current = null;
    if (press.dragging) onDragEnd(-1, -1);
  };

  const dragProps = (action: ActionDef) => ({
    onPointerDown: handlePointerDown(action),
    onPointerMove: handlePointerMove,
    onPointerUp: handlePointerUp,
    onPointerCancel: handlePointerCancel,
    style: { touchAction: "none" as const },
  });

  return (
    <div className="w-[260px] shrink-0 bg-white dark:bg-gray-900 border-r border-gray-100 dark:border-gray-800 overflow-y-auto">
      <button
        type="button"
        onClick={() => setOpen((s) => !s)}
        className="flex items-center justify-between w-full px-4 py-4 text-sm font-semibold text-gray-400 dark:text-gray-500"
      >
        Add
        <ChevronDown size={15} className={open ? "" : "-rotate-90"} />
      </button>

      {open && (
        <div className="px-3 pb-6 space-y-5">
          {disabled && (
            <p className="px-3 py-2 rounded-lg bg-indigo-50 dark:bg-indigo-500/10 text-[11px] leading-snug text-indigo-700 dark:text-indigo-300">
              Click a "+" on the canvas to add a step there, or press-and-drag any block below straight onto one.
            </p>
          )}
          <div className="grid grid-cols-3 gap-2">
            {QUICK_ACTIONS.map((qa) => {
              const Icon = QUICK_ICONS[qa.id] || HelpCircle;
              const isPlanned = qa.status === "planned";
              return (
                <button
                  key={qa.id}
                  type="button"
                  {...dragProps(qa)}
                  title={isPlanned ? "Coming soon" : 'Click a "+" slot first, or press and drag this onto one'}
                  className={cn(
                    "relative flex flex-col items-center gap-1.5 py-3 rounded-xl border border-gray-200 dark:border-gray-700 transition-colors select-none",
                    isPlanned
                      ? "opacity-40 cursor-not-allowed"
                      : "hover:border-indigo-300 hover:bg-indigo-50/50 dark:hover:bg-indigo-500/10 cursor-grab active:cursor-grabbing",
                    !isPlanned && disabled && "opacity-40"
                  )}
                >
                  <Icon size={16} className="text-gray-600 dark:text-gray-300" />
                  <span className="text-xs font-semibold text-gray-800 dark:text-gray-100">{qa.nodeLabel}</span>
                </button>
              );
            })}
          </div>

          {ACTION_CATEGORIES.map((cat) => (
            <div key={cat.id}>
              <div className="h-px bg-gray-100 dark:bg-gray-800 mb-4" />
              <p className="text-xs font-semibold text-gray-400 dark:text-gray-500 mb-2.5">{cat.label}</p>
              {cat.nodes.length === 0 ? (
                <p className="text-[11px] text-gray-300 dark:text-gray-600 italic pb-1">Coming soon</p>
              ) : (
                <div className="space-y-2">
                  {cat.nodes.map((node) => {
                    const isPlanned = node.status === "planned";
                    return (
                      <button
                        key={node.id}
                        type="button"
                        {...dragProps(node)}
                        title={isPlanned ? "Coming soon" : 'Click a "+" slot first, or press and drag this onto one'}
                        className={cn(
                          "flex items-center justify-between w-full px-3.5 py-2.5 rounded-lg bg-gray-50 dark:bg-gray-800 border border-gray-100 dark:border-gray-700 text-sm font-medium text-gray-700 dark:text-gray-200 transition-colors text-left select-none",
                          isPlanned
                            ? "opacity-40 cursor-not-allowed"
                            : "hover:border-indigo-300 hover:bg-indigo-50 dark:hover:bg-indigo-500/10 cursor-grab active:cursor-grabbing",
                          !isPlanned && disabled && "opacity-40"
                        )}
                      >
                        {node.paletteLabel}
                        {isPlanned ? (
                          <span className="text-[10px] font-semibold text-gray-400 dark:text-gray-500 shrink-0">Soon</span>
                        ) : (
                          <ChevronRight size={14} className="text-gray-400 shrink-0" />
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AddPanel;
